<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject } from 'vue';
    import ShareIcons from '@shared/components/ShareIcons.vue';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import EmptyState from '@shared/components/EmptyState.vue';
    import { formatDate, formatDateTime } from '@shared/utils/formatDateTime';
    import { EventStatusNames } from '@views/Event/EventTypes';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { eventKey } from '@views/Event/EventEditor/event-editor-keys';

    defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const publicHost = computed(() => contextAccess.state.currentAccount!.PublicEventUrl.PublicHost);
    const mapsApiKey = computed(() => contextAccess.state.currentAccount!.GoogleMapsApiKey);
    const mapEmbedUrl = computed(() =>
        mapsApiKey.value && event?.value?.Address
            ? `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey.value}&q=${encodeURIComponent(event?.value.Address)}`
            : null
    );

    // Start/End arrive already converted to the event's own TimeZone by the API; parse and
    // format them as UTC so the browser's local offset isn't applied on top (matches the
    // old Knockout view's moment.utc(val).format(...) behavior).
    const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' };
    const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' };

    const eventDateTimeDisplay = computed(() => {
        if (!event?.value?.Start) return '';

        const start = new Date(event?.value.Start);
        const end = event?.value.End ? new Date(event?.value.End) : null;
        const label = `${$Resources.DateLabel}: ${formatDate(start, dateOptions)} ${formatDateTime(start, timeOptions)}`;

        if (!end) return label;

        const isSameDay =
            start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth() && start.getUTCDate() === end.getUTCDate();
        if (isSameDay) return `${label} – ${formatDateTime(end, timeOptions)}`;

        return `${label} – ${formatDate(end, dateOptions)} ${formatDateTime(end, timeOptions)}`;
    });

    const landingPageUrl = computed(() => `${publicHost.value}/Events/${event?.value?.EventId}`);
    const landingPageDisabled = computed(() => !event?.value?.LandingPageIsPublished || event?.value?.Status !== EventStatusNames.Active);
    const signUpFormUrl = computed(() => `${publicHost.value}/Surveys/${event?.value?.DefaultSurveyId}`);
    const guestListUrl = computed(() => `${window.location.origin}/Events/${event?.value?.EventId}/Attendance`);
</script>

<template>
    <div class="max-1200">
        <h2 class="m-0-t">{{ $Resources.EventDashboardHeading }}</h2>

        <div class="event-hero m-lg-b" :style="{ 'background-image': event?.HeroImageUrl ? `url(${event?.HeroImageUrl})` : '' }">
            <div v-if="event?.Start" class="event-date">
                <span class="day h2">{{ formatDate(event?.Start, { day: 'numeric', timeZone: 'UTC' }) }}</span>
                <span class="month text-uppercase-first">{{ formatDate(event?.Start, { month: 'long', timeZone: 'UTC' }) }}</span>
            </div>
            <div class="event-hero-content c-white">
                <h1 class="w-full">{{ event?.Name || $Resources.EventNoNamePlaceholder }}</h1>

                <div v-if="event?.Start" class="d-flex gap-xs align-items-start">
                    <EventIcon icon="event" />
                    {{ eventDateTimeDisplay }}
                </div>

                <div v-if="event?.Address || event?.Venue" class="d-flex gap-xs align-items-start">
                    <EventIcon icon="location" />

                    <template v-if="event?.Address">{{ $Resources.EventLocationPlaceLabel }}: {{ event?.Address }}</template>
                    <template v-if="event?.Venue">{{ event?.Address ? ' (' + event?.Venue + ')' : event?.Venue }}</template>
                </div>
            </div>
        </div>

        <div class="section-summary d-flex flex-wrap justify-content-between gap-lg">
            <div class="event-description-colum m-lg-b">
                <EmptyState
                    v-if="!event?.PublicDescription"
                    :title="$Resources.EventDescriptionIsMissingHeading"
                    :body="$Resources.EventDescriptionIsMissingText">
                    <template #actions>
                        <div class="form-group m-sm-b">
                            <RouterLink :to="{ name: 'EventEditorDetailsNameAndDescription', params: { eventId } }" class="btn btn-info">
                                {{ $Resources.EventAddMissingDataBtnText }}
                            </RouterLink>
                        </div>
                    </template>
                </EmptyState>

                <div class="tiptap-viewmode" v-html="event?.PublicDescription"></div>
            </div>
            <div class="event-details-column">
                <ul class="list-unstyled m-lg-b">
                    <li class="m-lg-b">
                        <a
                            role="button"
                            class="btn btn-primary btn-block btn-flex"
                            :class="{ 'disabled pe-none': landingPageDisabled }"
                            :href="landingPageUrl"
                            target="_blank"
                            rel="noopener">
                            {{ $Resources.EventLinkToLandingPageLabel }}
                            <EventIcon icon="openInNew" :size="16" />
                            <small v-if="!event?.LandingPageIsPublished">({{ $Resources.EventLandingPageDisabledLabel }})</small>
                        </a>
                    </li>
                    <li class="m-lg-b" v-if="event">
                        <ShareIcons :label="$Resources.ShareEventLabel" size="small" type="Events" :id="event.EventId" />
                    </li>
                    <li v-if="event?.Status === EventStatusNames.Active && event?.DefaultSurveyId" class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="form" />
                        <a :href="signUpFormUrl" target="_blank" rel="noopener" class="d-flex gap-xs">
                            {{ $Resources.EventLinkToDefaultSignUpForm }}
                            <EventIcon icon="openInNew" :size="16" />
                        </a>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="list" />
                        <a :href="guestListUrl" target="_blank" rel="noopener" class="d-flex gap-xs">
                            {{ $Resources.EventLinkToPublicGuestList }}
                            <EventIcon icon="openInNew" :size="16" />
                        </a>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="event" />

                        <span class="flex-1" v-if="event?.Start">
                            {{ $Resources.DateLabel }}: {{ formatDate(event.Start, dateOptions) }}
                            <small v-if="event.End">({{ $Resources.EventEndsLabel }}: {{ formatDate(event.End, dateOptions) }})</small>
                        </span>
                        <EmptyState v-else :title="$Resources.EventDateIsMissingHeading" class="flex-1 m-0-b" hideIcon headingClasses="h5 m-0-t">
                            <template #actions>
                                <RouterLink :to="{ name: 'EventEditorDetailsStartAndEnd', params: { eventId } }" class="btn btn-info btn-sm">
                                    {{ $Resources.EventAddMissingDataBtnText }}
                                </RouterLink>
                            </template>
                        </EmptyState>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="schedule" />

                        <span class="flex-1" v-if="event?.Start">
                            {{ $Resources.TimeLabel }}: {{ formatDateTime(event.Start, timeOptions) }}
                            <small v-if="event.End">({{ $Resources.EventEndsLabel }}: {{ formatDateTime(event.End, timeOptions) }})</small>
                        </span>
                        <EmptyState v-else :title="$Resources.EventTimeIsMissingHeading" class="flex-1 m-0-b" hideIcon headingClasses="h5 m-0-t">
                            <template #actions>
                                <RouterLink :to="{ name: 'EventEditorDetailsStartAndEnd', params: { eventId } }" class="btn btn-info btn-sm">
                                    {{ $Resources.EventAddMissingDataBtnText }}
                                </RouterLink>
                            </template>
                        </EmptyState>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="location" />

                        <span class="flex-1" v-if="event?.Address || event?.Venue">
                            <template v-if="event?.Address">{{ $Resources.EventLocationPlaceLabel }}: {{ event?.Address }}</template>
                            <template v-if="event?.Venue">{{ event?.Address ? ' (' + event?.Venue + ')' : event?.Venue }}</template>

                            <iframe
                                v-if="mapEmbedUrl"
                                id="dashboard-map"
                                class="d-block m-sm-t"
                                width="600"
                                height="150"
                                frameborder="0"
                                style="border: 0; width: 100%"
                                :src="mapEmbedUrl"
                                allowfullscreen></iframe>
                        </span>

                        <EmptyState v-else :title="$Resources.EventLocationIsMissingHeading" class="flex-1 m-0-b" hideIcon headingClasses="h5 m-0-t">
                            <template #actions>
                                <RouterLink :to="{ name: 'EventEditorDetailsLocation', params: { eventId } }" class="btn btn-info btn-sm">
                                    {{ $Resources.EventAddMissingDataBtnText }}
                                </RouterLink>
                            </template>
                        </EmptyState>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b">
                        <EventIcon icon="person" />

                        <span v-if="event?.Organizer">
                            {{ $Resources.EventContactPersonLabel }}: {{ event?.Organizer }}
                            <small v-if="event?.Email || event?.Phone">({{ [event?.Email, event?.Phone].filter(Boolean).join(', ') }})</small>
                        </span>

                        <EmptyState v-else :title="$Resources.EventContactIsMissingHeading" class="flex-1 m-0-b" hideIcon headingClasses="h5 m-0-t">
                            <template #actions>
                                <RouterLink :to="{ name: 'EventEditorDetailsContactPerson', params: { eventId } }" class="btn btn-info btn-sm">
                                    {{ $Resources.EventAddMissingDataBtnText }}
                                </RouterLink>
                            </template>
                        </EmptyState>
                    </li>
                    <li class="d-flex gap-sm align-items-start m-lg-b" v-if="event && event?.Tags.length !== 0">
                        <EventIcon icon="sell" />
                        <TagsList :tags="event.Tags" readonly />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .event-description-colum {
        flex: 1 1 400px;
        min-width: 400px;
        max-width: 700px;
    }

    .event-details-column {
        flex: 1 1 300px;
        min-width: 300px;
        max-width: 400px;
    }
</style>
